import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import BasketballCourt from "@/components/BasketballCourt";
import { SHOT_TYPE_MAP } from "@/lib/zones";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const CHART_TOOLTIP_STYLE = { background: "#0A0A0A", border: "1px solid #27272A", borderRadius: 4 };
const CHART_LABEL_STYLE = { color: "#fff" };
const CHART_DOT_STYLE = { fill: "#FF3B30" };

export default function AthleteDashboard() {
  const { user } = useAuth();
  const params = useParams();
  const athleteId = params.athleteId || user?.athlete_id;
  const [athlete, setAthlete] = useState(null);
  const [stats, setStats] = useState(null);
  const [evolution, setEvolution] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!athleteId) return;
    (async () => {
      try {
        const [a, s, e] = await Promise.all([
          api.get(`/athletes/${athleteId}`),
          api.get(`/stats/athlete/${athleteId}`),
          api.get(`/stats/evolution/${athleteId}`),
        ]);
        setAthlete(a.data);
        setStats(s.data);
        setEvolution(e.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [athleteId]);

  const exportPDF = async () => {
    const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
      import("jspdf"),
      import("html2canvas"),
    ]);
    const node = document.getElementById("passport-export");
    if (!node) return;
    const canvas = await html2canvas(node, { backgroundColor: "#0A0A0A", scale: 2 });
    const img = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "pt", "a4");
    const pageW = pdf.internal.pageSize.getWidth();
    const imgH = (canvas.height * pageW) / canvas.width;
    pdf.addImage(img, "PNG", 0, 0, pageW, imgH);
    pdf.save(`passaporte_${athlete?.name || "atleta"}.pdf`);
  };

  if (loading) return <Layout><div className="text-zinc-400">Carregando...</div></Layout>;
  if (!athlete) return <Layout><div className="text-zinc-400">Atleta não encontrada.</div></Layout>;

  const s = stats || { total_made: 0, total_attempts: 0, total_points: 0, pct: 0, by_zone: {}, by_type: {} };

  return (
    <Layout>
      <div className="space-y-6" data-testid="athlete-passport">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#FF3B30]">Passaporte do Atleta</div>
            <h1 className="font-display font-black text-5xl sm:text-6xl uppercase tracking-tight mt-1">{athlete.name}</h1>
          </div>
          <Button
            onClick={exportPDF}
            data-testid="export-pdf-btn"
            className="bg-[#FF3B30] hover:bg-red-600 text-white font-bold uppercase tracking-wide rounded-sm"
          >
            Exportar PDF
          </Button>
        </div>

        <div id="passport-export" className="space-y-6">
          {/* Profile header */}
          <div className="bg-[#141414] border border-zinc-800 rounded-md p-6 flex flex-wrap items-center gap-6">
            {athlete.picture ? (
              <img src={athlete.picture} alt="" className="w-24 h-24 rounded-full object-cover border-2 border-[#FF3B30]" referrerPolicy="no-referrer" />
            ) : (
              <div className="w-24 h-24 rounded-full bg-zinc-800 flex items-center justify-center text-3xl font-display font-black">
                {athlete.name?.[0]?.toUpperCase()}
              </div>
            )}
            <div className="flex-1">
              <div className="text-xs uppercase tracking-widest text-zinc-500 font-bold">
                {athlete.position || "Atleta"} {athlete.number ? `• #${athlete.number}` : ""}
              </div>
              <div className="font-display font-black text-3xl uppercase mt-1">{athlete.name}</div>
            </div>
            <div className="grid grid-cols-3 gap-6">
              <Stat label="Aproveitamento" value={`${s.pct}%`} />
              <Stat label="Acertos" value={s.total_made} />
              <Stat label="Arremessos" value={s.total_attempts} />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-[#141414] border border-zinc-800 rounded-md p-6">
              <h2 className="font-display font-bold text-2xl uppercase tracking-tight mb-4">Mapa de Calor</h2>
              <BasketballCourt zoneStats={s.by_zone} />
            </div>

            <div className="bg-[#141414] border border-zinc-800 rounded-md p-6">
              <h2 className="font-display font-bold text-2xl uppercase tracking-tight mb-4">Por Tipo</h2>
              <div className="space-y-2">
                {Object.entries(s.by_type).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between p-3 bg-[#0A0A0A] border border-zinc-800 rounded-sm">
                    <span className="text-sm font-bold">{SHOT_TYPE_MAP[k] || k}</span>
                    <div className="text-right">
                      <div className="font-display font-black text-lg">{v.attempts > 0 ? `${v.pct}%` : "—"}</div>
                      <div className="text-xs text-zinc-500">{v.made}/{v.attempts}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Evolution */}
          <div className="bg-[#141414] border border-zinc-800 rounded-md p-6">
            <h2 className="font-display font-bold text-2xl uppercase tracking-tight mb-4">Evolução por Dia</h2>
            {evolution.length === 0 ? (
              <p className="text-zinc-500 text-sm">Sem dados de evolução ainda. Registre arremessos para começar.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={evolution}>
                  <CartesianGrid stroke="#27272A" strokeDasharray="3 3" />
                  <XAxis dataKey="date" stroke="#A1A1AA" fontSize={12} />
                  <YAxis stroke="#A1A1AA" fontSize={12} domain={[0, 100]} />
                  <Tooltip
                    contentStyle={CHART_TOOLTIP_STYLE}
                    labelStyle={CHART_LABEL_STYLE}
                  />
                  <Line type="monotone" dataKey="pct" stroke="#FF3B30" strokeWidth={3} dot={CHART_DOT_STYLE} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Stat({ label, value }) {
  return (
    <div className="text-center">
      <div className="font-display font-black text-3xl tracking-tighter">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mt-1">{label}</div>
    </div>
  );
}
